/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { Mat4, Vec3 } from '../../../mol-math/linear-algebra';
import { Box, PerforatedBox } from '../../../mol-geo/primitive/box';
import { OctagonalPyramid, PerforatedOctagonalPyramid } from '../../../mol-geo/primitive/pyramid';
import { Star } from '../../../mol-geo/primitive/star';
import { Octahedron, PerforatedOctahedron } from '../../../mol-geo/primitive/octahedron';
import { DiamondPrism, PentagonalPrism, ShiftedHexagonalPrism, HexagonalPrism, HeptagonalPrism } from '../../../mol-geo/primitive/prism';
import { Structure, StructureElement, Unit } from '../../../mol-model/structure';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { getSaccharideShape } from '../../../mol-model/structure/structure/carbohydrates/constants';
import { addSphere } from '../../../mol-geo/geometry/mesh/builder/sphere';
import { ComplexMeshParams, ComplexMeshVisual } from '../complex-visual';
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { LocationIterator } from '../../../mol-geo/util/location-iterator';
import { OrderedSet, Interval } from '../../../mol-data/int';
import { EmptyLoci } from '../../../mol-model/loci';
import { getAltResidueLociFromId } from './util/common';
import { BaseGeometry } from '../../../mol-geo/geometry/base';
var t = Mat4.identity();
var sVec = Vec3.zero();
var pd = Vec3.zero();
var SideFactor = 2 * 0.806; // 0.806 == Math.cos(Math.PI / 4)

var box = Box();
var perforatedBox = PerforatedBox();
var octagonalPyramid = OctagonalPyramid();
var perforatedOctagonalPyramid = PerforatedOctagonalPyramid();
var star = Star({
  outerRadius: 1,
  innerRadius: 0.5,
  thickness: 0.5,
  pointCount: 5
});
var octahedron = Octahedron();
var perforatedOctahedron = PerforatedOctahedron();
var diamondPrism = DiamondPrism();
var pentagonalPrism = PentagonalPrism();
var hexagonalPrism = HexagonalPrism();
var shiftedHexagonalPrism = ShiftedHexagonalPrism();
var heptagonalPrism = HeptagonalPrism();

function createCarbohydrateSymbolMesh(ctx, structure, theme, props, mesh) {
  var builderState = MeshBuilder.createState(256, 128, mesh);
  var detail = props.detail,
      sizeFactor = props.sizeFactor;
  var carbohydrates = structure.carbohydrates;
  var n = carbohydrates.elements.length;
  var l = StructureElement.Location.create(structure);

  for (var i = 0; i < n; ++i) {
    var c = carbohydrates.elements[i];
    var ring = c.unit.rings.all[c.ringIndex];
    var shapeType = getSaccharideShape(c.component.type, ring.length);
    l.unit = c.unit;
    l.element = c.unit.elements[ring[0]];
    var size = theme.size.size(l);
    var radius = size * sizeFactor;
    var side = size * sizeFactor * SideFactor;
    var _a = c.geometry,
        center = _a.center,
        normal = _a.normal,
        direction = _a.direction;
    Vec3.add(pd, center, direction);
    Mat4.targetTo(t, center, pd, normal);
    Mat4.setTranslation(t, center);
    builderState.currentGroup = i * 2;

    switch (shapeType) {
      case 0
      /* FilledSphere */
      :
        addSphere(builderState, center, radius, detail);
        break;

      case 1
      /* FilledCube */
      :
        Mat4.scaleUniformly(t, t, side);
        MeshBuilder.addPrimitive(builderState, t, box);
        break;

      case 2
      /* CrossedCube */
      :
        Mat4.scaleUniformly(t, t, side);
        MeshBuilder.addPrimitive(builderState, t, perforatedBox);
        Mat4.mul(t, t, Mat4.rotZ90X180);
        builderState.currentGroup += 1;
        MeshBuilder.addPrimitive(builderState, t, perforatedBox);
        break;

      case 4
      /* FilledCone */
      :
        Mat4.scaleUniformly(t, t, side * 1.2);
        MeshBuilder.addPrimitive(builderState, t, octagonalPyramid);
        break;

      case 5
      /* DevidedCone */
      :
        Mat4.scaleUniformly(t, t, side * 1.2);
        MeshBuilder.addPrimitive(builderState, t, perforatedOctagonalPyramid);
        Mat4.mul(t, t, Mat4.rotZ90);
        builderState.currentGroup += 1;
        MeshBuilder.addPrimitive(builderState, t, perforatedOctagonalPyramid);
        break;

      case 6
      /* FlatBox */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, box);
        break;

      case 7
      /* FilledStar */
      :
        Mat4.scaleUniformly(t, t, side);
        Mat4.mul(t, t, Mat4.rotZY90);
        MeshBuilder.addPrimitive(builderState, t, star);
        break;

      case 8
      /* FilledDiamond */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side * 1.4, side * 1.4, side * 1.4));
        MeshBuilder.addPrimitive(builderState, t, octahedron);
        break;

      case 3
      /* DividedDiamond */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side * 1.4, side * 1.4, side * 1.4));
        MeshBuilder.addPrimitive(builderState, t, perforatedOctahedron);
        Mat4.mul(t, t, Mat4.rotY90);
        builderState.currentGroup += 1;
        MeshBuilder.addPrimitive(builderState, t, perforatedOctahedron);
        break;

      case 9
      /* FlatDiamond */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side / 2, side / 2));
        MeshBuilder.addPrimitive(builderState, t, diamondPrism);
        break;

      case 12
      /* DiamondPrism */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, diamondPrism);
        break;

      case 13
      /* PentagonalPrism */
      :
      case 11
      /* Pentagon */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, pentagonalPrism);
        break;

      case 14
      /* HexagonalPrism */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, hexagonalPrism);
        break;

      case 15
      /* HeptagonalPrism */
      :
        Mat4.mul(t, t, Mat4.rotZY90);
        Mat4.scale(t, t, Vec3.set(sVec, side, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, heptagonalPrism);
        break;

      case 10
      /* FlatHexagon */
      :
      default:
        Mat4.mul(t, t, Mat4.rotZYZ90);
        Mat4.scale(t, t, Vec3.set(sVec, side / 1.5, side, side / 2));
        MeshBuilder.addPrimitive(builderState, t, shiftedHexagonalPrism);
        break;
    }
  }

  return MeshBuilder.getMesh(builderState);
}

export var CarbohydrateSymbolParams = __assign(__assign({}, ComplexMeshParams), {
  detail: PD.Numeric(0, {
    min: 0,
    max: 3,
    step: 1
  }, BaseGeometry.CustomQualityParamInfo),
  sizeFactor: PD.Numeric(1.75, {
    min: 0,
    max: 10,
    step: 0.01
  })
});
export function CarbohydrateSymbolVisual(materialId) {
  return ComplexMeshVisual({
    defaultProps: PD.getDefaultValues(CarbohydrateSymbolParams),
    createGeometry: createCarbohydrateSymbolMesh,
    createLocationIterator: CarbohydrateElementIterator,
    getLoci: getCarbohydrateLoci,
    eachLocation: eachCarbohydrate,
    setUpdateState: function (state, newProps, currentProps) {
      state.createGeometry = newProps.sizeFactor !== currentProps.sizeFactor || newProps.detail !== currentProps.detail;
    }
  }, materialId);
}

function CarbohydrateElementIterator(structure) {
  var carbElements = structure.carbohydrates.elements;
  var groupCount = carbElements.length * 2;
  var instanceCount = 1;
  var location = StructureElement.Location.create(structure);

  function getLocation(groupIndex, instanceIndex) {
    var carb = carbElements[Math.floor(groupIndex / 2)];
    var ring = carb.unit.rings.all[carb.ringIndex];
    location.unit = carb.unit;
    location.element = carb.unit.elements[ring[0]];
    return location;
  }

  function isSecondary(elementIndex, instanceIndex) {
    return elementIndex % 2 === 1;
  }

  return LocationIterator(groupCount, instanceCount, 1, getLocation, true, isSecondary);
}
/** Return a Loci for the elements of the whole residue of a carbohydrate. */


function getCarbohydrateLoci(pickingId, structure, id) {
  var objectId = pickingId.objectId,
      groupId = pickingId.groupId;

  if (id === objectId) {
    var carb = structure.carbohydrates.elements[Math.floor(groupId / 2)];
    return getAltResidueLociFromId(structure, carb.unit, carb.residueIndex, carb.altId);
  }

  return EmptyLoci;
}
/** For each carbohydrate (usually a monosaccharide) when all its residue's elements are in a loci. */


function eachCarbohydrate(loci, structure, apply) {
  var getElementIndices = structure.carbohydrates.getElementIndices;
  var changed = false;
  if (!StructureElement.Loci.is(loci)) return false;
  if (!Structure.areEquivalent(loci.structure, structure)) return false;

  var _loop_1 = function (unit, indices) {
    if (!Unit.isAtomic(unit)) return "continue";
    OrderedSet.forEach(indices, function (v) {
      // TODO avoid duplicate calls to apply
      var elementIndices = getElementIndices(unit, unit.elements[v]);

      for (var i = 0, il = elementIndices.length; i < il; ++i) {
        if (apply(Interval.ofSingleton(elementIndices[i] * 2))) changed = true;
      }
    });
  };

  for (var _i = 0, _a = loci.elements; _i < _a.length; _i++) {
    var _b = _a[_i],
        unit = _b.unit,
        indices = _b.indices;

    _loop_1(unit, indices);
  }

  return changed;
}